import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BIRTH_WEIGHT_GRAMS, DELIVERY_OUTCOME_LABELS } from '@wodi/domain';
import type { DeliveryBabyView, DeliveryView, LabourRecordView } from '@wodi/shared';
import { Button, Notice, RadioGroup, Select, Tag, TextField, type TagState } from '@wodi/ui';
import { useRef, useState } from 'react';

import { maternityKeys, startBirthNotification } from '../api/maternity.ts';
import { can, sessionQuery } from '../api/session.ts';
import { birthFormOptionsQuery } from '../api/vital-events.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { toasts } from '../toasts.ts';
import { gramsText } from '../vital-events/words.ts';
import { errorText } from './common.tsx';
import styles from './maternity.module.css';

// A baby on the delivery and its birth notification (docs/plans/phase2-maternity-mch.md §5;
// docs/workflows/maternity.md §4): not started, recorded, or entered in error, with a link.
// A live-born baby's notification is started here, prefilled from the delivery; the time,
// mother and birth order are the delivery's, and so are the sex and weight it recorded. A
// stillbirth is not notified as a birth. For review by the clinical safety lead.

const NOTIFICATION_TAGS: Record<'recorded' | 'entered_in_error', [string, TagState]> = {
  recorded: ['Recorded', 'done'],
  entered_in_error: ['Entered in error', 'neutral'],
};

function useSetView(labourId: string) {
  const queryClient = useQueryClient();
  return (next: LabourRecordView) => {
    queryClient.setQueryData(maternityKeys.labour(labourId), next);
    void queryClient.invalidateQueries({ queryKey: maternityKeys.patient(next.patientId) });
    void queryClient.invalidateQueries({ queryKey: ['births'] });
    void queryClient.invalidateQueries({ queryKey: ['patients'] });
  };
}

function BirthNotificationForm({
  baby,
  delivery,
  labourId,
  onDone,
}: {
  baby: DeliveryBabyView;
  delivery: DeliveryView;
  labourId: string;
  onDone: () => void;
}) {
  const online = useOnline();
  const setView = useSetView(labourId);
  const options = useQuery(birthFormOptionsQuery);
  const requestId = useRef(crypto.randomUUID());
  const [sex, setSex] = useState('');
  const [weight, setWeight] = useState('');
  const [attendant, setAttendant] = useState<string | null>(null);
  const attendants = options.data?.attendants ?? [];
  const deliveryAttendant = attendants.some((a) => a.id === delivery.attendant.id)
    ? delivery.attendant.id
    : null;
  const chosenAttendant = attendant ?? deliveryAttendant;
  const chosenSex = options.data?.sexes.find((s) => `${s.system}|${s.code}` === sex);
  const grams = Number(weight);
  const weightOk =
    Number.isInteger(grams) && grams >= BIRTH_WEIGHT_GRAMS.min && grams <= BIRTH_WEIGHT_GRAMS.max;
  const save = useMutation({
    mutationFn: () =>
      startBirthNotification(baby.id, {
        clientRequestId: requestId.current,
        attendantUserId: chosenAttendant ?? '',
        sex:
          baby.sex === null && chosenSex
            ? { system: chosenSex.system, code: chosenSex.code }
            : null,
        birthWeightGrams: baby.birthWeightGrams === null ? grams : null,
      }),
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Birth notification recorded' });
      onDone();
    },
  });
  if (options.isPending) return <p className={styles.meta}>Loading the birth notification form…</p>;
  if (options.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void options.refetch()}>Try again</Button>}
      >
        {errorText(options.error, "The birth notification form couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const ready =
    online &&
    chosenAttendant !== null &&
    (baby.sex !== null || chosenSex !== undefined) &&
    (baby.birthWeightGrams !== null || weightOk) &&
    !save.isPending;
  const several = delivery.babies.length > 1;
  return (
    <form
      className={styles.form}
      aria-label={`Start birth notification, baby ${String(baby.birthOrder)}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <p className={styles.meta}>
        {`From the delivery: born ${formatInstant(baby.bornAt)}${several ? `, baby ${String(baby.birthOrder)} of ${String(delivery.babies.length)}` : ''}, to the patient on this record. The baby is registered as a new patient, linked to her.`}
      </p>
      {baby.sex === null ? (
        <RadioGroup
          label="Sex"
          orientation="horizontal"
          value={sex}
          onChange={setSex}
          options={options.data.sexes.map((s) => ({
            id: `${s.system}|${s.code}`,
            label: s.display,
          }))}
        />
      ) : (
        <p>{`Sex: ${baby.sex.display}, as recorded on the delivery.`}</p>
      )}
      {baby.birthWeightGrams === null ? (
        <TextField
          label="Birth weight"
          unit="g"
          inputMode="numeric"
          value={weight}
          onChange={setWeight}
          width="sm"
          {...(weight !== '' && !weightOk
            ? { errorMessage: 'Write the weight in whole grams, like 3150.' }
            : {})}
        />
      ) : (
        <p>{`Birth weight: ${gramsText(baby.birthWeightGrams)}, as recorded on the delivery.`}</p>
      )}
      <Select
        label="Attended by"
        description="People who may record births here."
        placeholder="Choose one"
        options={attendants.map((a) => ({ id: a.id, label: a.displayName }))}
        value={chosenAttendant}
        onChange={(id) => {
          setAttendant(typeof id === 'string' ? id : null);
        }}
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. Recording a birth notification waits for the connection; nothing is
          saved on this device.
        </Notice>
      ) : null}
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The birth notification was not recorded. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!ready}
          isPending={save.isPending}
          pendingLabel="Recording…"
        >
          Record birth notification
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function NotificationState({
  baby,
  delivery,
  mayStart,
  labourId,
}: {
  baby: DeliveryBabyView;
  delivery: DeliveryView;
  mayStart: boolean;
  labourId: string;
}) {
  const online = useOnline();
  const [starting, setStarting] = useState(false);
  if (baby.outcome !== 'live_birth') {
    return <p className={styles.meta}>Birth notification: not made for a stillbirth.</p>;
  }
  const notification = baby.birthNotification;
  if (notification !== null) {
    const [word, shape] = NOTIFICATION_TAGS[notification.status];
    return (
      <div className={styles.actions}>
        <span>Birth notification:</span>
        <Tag state={shape}>{word}</Tag>
        <Link to="/births/$birthId" params={{ birthId: notification.id }}>
          Open birth notification
        </Link>
        {baby.babyPatientId === null ? null : (
          <Link to="/patients/$patientId" params={{ patientId: baby.babyPatientId }}>
            Baby&apos;s record
          </Link>
        )}
      </div>
    );
  }
  if (starting) {
    return (
      <BirthNotificationForm
        baby={baby}
        delivery={delivery}
        labourId={labourId}
        onDone={() => {
          setStarting(false);
        }}
      />
    );
  }
  return (
    <div className={styles.actions}>
      <span>Birth notification: not started.</span>
      {mayStart ? (
        <Button
          dense
          isDisabled={!online}
          onPress={() => {
            setStarting(true);
          }}
        >
          Start birth notification
        </Button>
      ) : null}
    </div>
  );
}

export function BabyLine({
  baby,
  delivery,
  mayRecord,
  labourId,
}: {
  baby: DeliveryBabyView;
  delivery: DeliveryView;
  /** May record on this delivery (maternity.record, and the delivery stands). */
  mayRecord: boolean;
  labourId: string;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const mayStart = mayRecord && session !== null && can(session, 'births.record');
  const facts = [
    DELIVERY_OUTCOME_LABELS[baby.outcome],
    baby.sex?.display ?? 'sex not recorded',
    baby.birthWeightGrams === null ? 'weight not recorded' : gramsText(baby.birthWeightGrams),
    baby.apgar1 === null && baby.apgar5 === null
      ? null
      : `Apgar ${baby.apgar1 === null ? '–' : String(baby.apgar1)} and ${baby.apgar5 === null ? '–' : String(baby.apgar5)}`,
    baby.note,
  ].filter((x) => x !== null);
  return (
    <li className={styles.baby}>
      <p
        className={styles.legend}
      >{`Baby ${String(baby.birthOrder)}, born ${formatInstant(baby.bornAt)}`}</p>
      <p className={styles.meta}>{facts.join('; ')}</p>
      <NotificationState baby={baby} delivery={delivery} mayStart={mayStart} labourId={labourId} />
    </li>
  );
}
